import React, { useEffect } from 'react';
import { Route, Routes as RouterRoutes, useLocation } from 'react-router';
import { Routes } from '@buy-viasat/redux/src/navigator';
import { Serviceability } from '@buy-viasat/serviceability';
import { Product } from '@buy-viasat/types/build/bv';

import { BeamModalSelector } from '@buy-viasat/react';
import { BeamProvider, ToastContainer, useBreakpoints } from '@vst/beam';

// Lazy load routes after the home page
const PlanGrid = React.lazy(() => import('@buy-viasat/plans').then((module) => ({ default: module.Plans })));
const PersonalInformation = React.lazy(() =>
  import('@buy-viasat/account-information').then((module) => ({ default: module.AccountInformation })),
);
const CreditVerification = React.lazy(() =>
  import('@buy-viasat/credit-verification').then((module) => ({ default: module.CreditVerification })),
);
const PaymentInformation = React.lazy(() =>
  import('@buy-viasat/payment-information').then((module) => ({ default: module.PaymentInformation })),
);
const OrderConfirmation = React.lazy(() =>
  import('@buy-viasat/order-confirmation').then((module) => ({ default: module.OrderConfirmation })),
);
const OptionalServices = React.lazy(() => import('@buy-viasat/addons').then((module) => ({ default: module.Addons })));

const ProductDisclosures = React.lazy(() => import('../../containers/disclosures'));

const ScheduleInstallation = React.lazy(() =>
  import('@buy-viasat/schedule-installation').then((module) => ({ default: module.ScheduleInstallation })),
);

type BeamFlowNavigatorProps = {
  onPlanEdit: () => void;
  onPlanDetails: () => void;
  onPlanSelect: (product: Product) => void;
};

export const BeamFlowNavigator = ({ onPlanEdit, onPlanDetails, onPlanSelect }: BeamFlowNavigatorProps): JSX.Element => {
  const { pathname } = useLocation();

  const toastContainerDistance = useBreakpoints().viewport.smallerOrWithin.sm ? 22 : 95;

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, [pathname]);

  return (
    <BeamProvider withToastProvider>
      <ToastContainer position="top-right" distance={toastContainerDistance} />
      {/* Paths here are relative to /:locale/:customerType/* defined in Navigator/index.tsx. */}
      <RouterRoutes>
        {/* index = default route when no sub-path matches, equivalent to exact path="/". */}
        <Route index element={<Serviceability />} />
        <Route
          path={Routes.PLAN}
          element={<PlanGrid onPlanEdit={onPlanEdit} onPlanSelect={onPlanSelect} onPlanDetails={onPlanDetails} />}
        />
        <Route
          path={Routes.PLAN_GRID}
          element={<PlanGrid onPlanEdit={onPlanEdit} onPlanSelect={onPlanSelect} onPlanDetails={onPlanDetails} />}
        />
        <Route path={Routes.PERSONAL_INFORMATION} element={<PersonalInformation />} />
        <Route path={Routes.ADDONS} element={<OptionalServices />} />
        <Route path={Routes.DISCLOSURES} element={<ProductDisclosures />} />
        <Route path={Routes.CREDIT_CHECK} element={<CreditVerification />} />
        <Route path={Routes.SCHEDULE_INSTALLATION} element={<ScheduleInstallation />} />
        <Route path={Routes.PAYMENT_INFORMATION} element={<PaymentInformation />} />
        <Route path={Routes.ORDER_SUMMARY} element={<OrderConfirmation />} />
        <Route path="*" element={<Serviceability />} />
      </RouterRoutes>
      <BeamModalSelector />
    </BeamProvider>
  );
};
